import React, { useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, ShoppingBag, Calendar, BookOpen, MessageSquare, LogOut, UtensilsCrossed, Menu as MenuIcon, X, Users } from 'lucide-react';
import { useAdmin } from '../context/AdminContext';

export const AdminLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { staff, logout } = useAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = React.useState(false);

  useEffect(() => {
    if (!staff) {
      navigate('/admin/login');
    }
  }, [staff, navigate]);

  if (!staff) {
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const links = [
    { name: 'Dashboard', path: '/admin', icon: LayoutDashboard },
    { name: 'Orders', path: '/admin/orders', icon: ShoppingBag },
    { name: 'Reservations', path: '/admin/reservations', icon: Calendar },
    { name: 'Menu Manager', path: '/admin/menu', icon: BookOpen },
    { name: 'Reviews', path: '/admin/reviews', icon: MessageSquare },
    { name: 'Staff', path: '/admin/staff', icon: Users },
  ];

  return (
    <div className="min-h-screen bg-gray-100 flex overflow-x-hidden">
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 text-white transform transition-transform duration-300 ease-in-out ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0`}>
        <div className="p-6 flex justify-between items-center border-b border-gray-800">
          <div className="flex items-center gap-2">
            <UtensilsCrossed className="text-terracotta" />
            <span className="font-serif font-bold text-xl">ZP Admin</span>
          </div>
          <button onClick={() => setIsMobileOpen(false)} className="md:hidden text-gray-400 hover:text-white transition-colors">
            <X size={24} />
          </button>
        </div>

        <nav className="p-4 space-y-2">
          {links.map(link => {
            const isActive = link.path === '/admin'
              ? location.pathname === '/admin'
              : location.pathname.startsWith(link.path);

            return (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group ${isActive
                  ? 'bg-terracotta text-white shadow-lg shadow-terracotta/20'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`}
              >
                <link.icon size={20} className={`transition-transform duration-200 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`} />
                <span className="font-medium">{link.name}</span>
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 w-full p-6 border-t border-gray-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center font-bold text-lg">
              {staff.full_name.charAt(0)}
            </div>
            <div>
              <p className="font-bold text-sm">{staff.full_name}</p>
              <p className="text-xs text-gray-500 capitalize">{staff.role}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-gray-400 hover:text-red-400 text-sm font-medium w-full">
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        <header className="bg-white shadow-sm h-16 flex items-center px-6 md:hidden">
          <button onClick={() => setIsMobileOpen(true)} className="text-gray-600">
            <MenuIcon size={24} />
          </button>
          <span className="ml-4 font-bold text-gray-900">Admin Dashboard</span>
        </header>

        <main className="flex-1 overflow-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
};
